/* eslint-disable react-hooks/exhaustive-deps */

import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import gql from "graphql-tag";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { abortOrder, orderStripe } from "../../apollo/server";
import { Header } from "../../components/Header";
import ConfigurableValues from "../../config/constants";
import UserContext from "../../context/User";
import EmbedContext from "../../context/Embed";
import useStyles from "../Stripe/styles";

const ABORT_ORDER = gql`
  ${abortOrder}
`;

const ORDER_STRIPE = gql`
  ${orderStripe}
`;

function NMI() {
  const { SERVER_URL, NMI_TOKENIZATION_KEY } = ConfigurableValues();
  const classes = useStyles();
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id") ?? null;
  const navigate = useNavigate();
  const { clearCart, fetchOrders } = useContext(UserContext);
  // Embed mode: stay on the order's page instead of the full orders list
  const { isEmbed } = useContext(EmbedContext);

  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [collectReady, setCollectReady] = useState(false);
  const configuredRef = useRef(false);
  const paymentSucceeded = useRef(false);

  const [abortOrderMutation] = useMutation(ABORT_ORDER);

  // Delete the order if user leaves without paying
  useEffect(() => {
    return () => {
      if (!paymentSucceeded.current && id) {
        abortOrderMutation({ variables: { id } }).catch(() => {});
      }
    };
  }, [id]);

  const { data, loading, error } = useQuery(ORDER_STRIPE, {
    variables: { id },
    skip: !id,
  });

  const order = data?.orderStripe;
  const totalAmount = order
    ? parseFloat(order.orderAmount) + parseFloat(order.deliveryCharges || 0) + parseFloat(order.tipping || 0) + parseFloat(order.taxationAmount || 0)
    : 0;

  // Dynamically load Collect.js script once we have the order and tokenization key
  useEffect(() => {
    if (!NMI_TOKENIZATION_KEY || !order) return;
    if (document.getElementById("collect-js-script")) {
      setCollectReady(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "collect-js-script";
    script.src = "https://secure.nmi.com/token/Collect.js";
    script.setAttribute("data-tokenization-key", NMI_TOKENIZATION_KEY);
    script.setAttribute("data-variant", "inline");
    script.onload = () => setCollectReady(true);
    document.body.appendChild(script);
  }, [NMI_TOKENIZATION_KEY, order]);

  // Configure CollectJS once script is ready
  useEffect(() => {
    if (!collectReady || configuredRef.current || !order) return;
    if (!window.CollectJS) return;
    configuredRef.current = true;

    window.CollectJS.configure({
      variant: "inline",
      styleSniffer: false,
      fields: {
        ccnumber: { selector: "#nmi-ccnumber", placeholder: "Card Number" },
        ccexp: { selector: "#nmi-ccexp", placeholder: "MM / YY" },
        cvv: { selector: "#nmi-cvv", placeholder: "CVV" },
      },
      callback: async (response) => {
        setProcessing(true);
        setErrorMsg(null);
        try {
          const resp = await fetch(`${SERVER_URL}nmi/charge`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId: order.orderId,
              dbId: id,
              payment_token: response.token,
            }),
          });
          const result = await resp.json();
          if (result.success) {
            paymentSucceeded.current = true;
            clearCart();
            await fetchOrders();
            navigate(isEmbed ? `/order-detail/${id}` : "/orders", { replace: true });
          } else {
            setErrorMsg(result.error || "Payment declined. Please try again.");
            setProcessing(false);
          }
        } catch (err) {
          setErrorMsg("Network error. Please try again.");
          setProcessing(false);
        }
      },
    });
  }, [collectReady, order, id, SERVER_URL, navigate, fetchOrders, isEmbed]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!window.CollectJS || processing) return;
    setProcessing(true);
    setErrorMsg(null);
    window.CollectJS.startPaymentRequest();
  };

  if (error) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" justifyContent="center" alignItems="center" flex={1} style={{ minHeight: "55vh" }}>
            <Typography variant="h5" color="error">
              {error.message}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (!id) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" justifyContent="center" alignItems="center" flex={1} style={{ minHeight: "55vh" }}>
            <Typography variant="h5" color="error">
              Missing order ID
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (loading || !order) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ minHeight: "55vh" }}>
            <CircularProgress size={48} />
            <Typography variant="h6" style={{ marginTop: 20 }}>
              Loading order...
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className={classes.root}>
      <Header />
      <Grid container item className={classes.mainContainer} justifyContent="center">
        <Grid item xs={12} sm={10} md={8} lg={6}>
          <Paper className={classes.paymentCard} elevation={3}>
            <Typography variant="h5" className={classes.title}>
              Complete Your Payment
            </Typography>
            <Divider style={{ margin: "16px 0" }} />

            {/* Order Summary */}
            <Box className={classes.orderSummary}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Order #{order.orderId}</strong>
              </Typography>
              <Box className={classes.itemRow}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  ${parseFloat(order.orderAmount).toFixed(2)}
                </Typography>
              </Box>
              {parseFloat(order.deliveryCharges) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Delivery</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.deliveryCharges).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.taxationAmount) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Tax</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.taxationAmount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.tipping) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Tip</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.tipping).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.blackCardDiscount) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2" style={{ color: "#16a34a" }}>
                    Black Card Delivery Discount
                  </Typography>
                  <Typography variant="body2" style={{ color: "#16a34a", fontWeight: 600 }}>
                    -${parseFloat(order.blackCardDiscount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider style={{ margin: "8px 0" }} />
              <Box className={classes.itemRow}>
                <Typography variant="subtitle1">
                  <strong>Total</strong>
                </Typography>
                <Typography variant="subtitle1">
                  <strong>${totalAmount.toFixed(2)}</strong>
                </Typography>
              </Box>
            </Box>

            <Divider style={{ margin: "16px 0" }} />

            {/* NMI Collect.js Card Form */}
            <form onSubmit={handleSubmit}>
              <Typography variant="caption" display="block" style={{ marginBottom: 4 }}>
                Card Number
              </Typography>
              <div
                id="nmi-ccnumber"
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  padding: "10px 12px",
                  marginBottom: 12,
                  minHeight: 44,
                  background: "#fafafa",
                }}
              />
              <Box display="flex" gap={2}>
                <Box flex={1}>
                  <Typography variant="caption" display="block" style={{ marginBottom: 4 }}>
                    Expiry
                  </Typography>
                  <div
                    id="nmi-ccexp"
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      padding: "10px 12px",
                      marginBottom: 12,
                      minHeight: 44,
                      background: "#fafafa",
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" display="block" style={{ marginBottom: 4 }}>
                    CVV
                  </Typography>
                  <div
                    id="nmi-cvv"
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      padding: "10px 12px",
                      marginBottom: 12,
                      minHeight: 44,
                      background: "#fafafa",
                    }}
                  />
                </Box>
              </Box>
              {errorMsg && (
                <Typography color="error" variant="body2" className={classes.errorText}>
                  {errorMsg}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={processing || !collectReady}
                className={classes.payButton}
              >
                {processing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Pay $${totalAmount.toFixed(2)}`
                )}
              </Button>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(NMI);
